import React, { useState } from "react";
import { css } from "@emotion/css";
import type {
  RecalibrationDirection,
  UiInteractionSpec,
} from "../../types/state";

/**
 * Shown above an activity step that was regenerated from a "feels too
 * easy / too hard" tap.  A chip states the recalibration ("Made simpler /
 * tougher"); a quiet toggle lets the learner peek at the version it
 * replaced (read-only) so the re-do never feels like lost work.
 */

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 4px;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const chipStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74em;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--color-primary);
  background: var(--color-primary-muted);
  border: 1px solid var(--color-primary);
  border-radius: 999px;
  padding: 3px 10px;
`;

const toggleStyle = css`
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.8em;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: var(--color-text); }
`;

const previousStyle = css`
  font-size: 0.85em;
  color: var(--color-text-secondary);
  line-height: 1.5;
  background: var(--color-bg-subtle);
  border: 1px dashed var(--color-border-subtle);
  border-radius: 10px;
  padding: 10px 14px;
  white-space: pre-wrap;
`;

const previousLabelStyle = css`
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 4px;
`;

const COPY: Record<RecalibrationDirection, { glyph: string; label: string }> = {
  easier: { glyph: "↓", label: "Made simpler" },
  harder: { glyph: "↑", label: "Made tougher" },
};

/** Best-effort one-line summary of a (superseded) step for the peek. */
function summarize(it: UiInteractionSpec | undefined): string | null {
  if (!it) return null;
  const anyIt = it as unknown as Record<string, unknown> & {
    question?: string;
    prompt?: { question?: string };
    preamble?: string;
  };
  return (
    anyIt.question ??
    anyIt.prompt?.question ??
    anyIt.preamble ??
    null
  );
}

interface RecalibrationBannerProps {
  direction: RecalibrationDirection;
  /** The version this step replaced (for the read-only peek), if present. */
  previous?: UiInteractionSpec;
}

export const RecalibrationBanner: React.FC<RecalibrationBannerProps> = ({
  direction,
  previous,
}) => {
  const [open, setOpen] = useState(false);
  const copy = COPY[direction];
  const prevText = summarize(previous);

  return (
    <div className={wrapStyle} data-testid="recalibration-banner">
      <div className={rowStyle}>
        <span className={chipStyle} data-testid="recalibration-chip">
          <span aria-hidden="true">{copy.glyph}</span>
          {copy.label}
        </span>
        {prevText && (
          <button
            type="button"
            className={toggleStyle}
            onClick={() => setOpen((v) => !v)}
            data-testid="recalibration-toggle"
          >
            {open ? "Hide previous version" : "Show previous version"}
          </button>
        )}
      </div>
      {open && prevText && (
        <div className={previousStyle} data-testid="recalibration-previous">
          <div className={previousLabelStyle}>Previous version</div>
          {prevText}
        </div>
      )}
    </div>
  );
};
